import { AppNS } from 'app';
import { APP_VER } from 'app/core/config';
import { ReactNode } from 'react';

import { Box, SxProps, Theme, useTheme } from '@mui/material';

import LogoutButton from './LogoutButton';
import NavigationElement from './NavigationElement';

const getStyles = (theme: Theme): { [key: string]: SxProps } => {
  return {
    root: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      minHeight: '80px',
      '& .mainPanel': {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        padding: '16px 8px',
      },
      '& .settingsPanel': {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        padding: '16px 8px',
        '&:before': {
          position: 'absolute',
          top: 0,
          left: 0,
          content: '""',
          display: 'block',
          width: '100%',
          height: '1px',
          background: theme.palette.divider,
        },
      },
    },
    toolBar: {
      display: 'flex',
      flexDirection: 'row',
      alignContent: 'center',
      alignItems: 'flex-start',
      justifyContent: 'space-evenly',
      width: '100%',
      maxWidth: 360,
      alignSelf: 'center',

      '& a': {
        flexDirection: 'column',
        height: '100%',
        width: '70px',
        marginTop: 0,
        paddingTop: 2,
        paddingLeft: 0,
        color: theme.palette.text.primary,
        fontSize: '1.2rem',
        textAlign: 'center',
        '& .iconContainer': {
          color: theme.palette.primary.main,
          margin: 'auto',
          marginBottom: 1,
          width: '2em',
          '& svg': {
            width: '26px',
          },
          flex: '0 0 auto',
        },
        '& .labelContainer': {
          margin: 0,
          flex: '0 0 auto',
        },
        '&:hover, &.active': {
          background: 'none',
          color: '#213a6d',
          '&:after': {
            width: '100%',
            height: '4px',
            position: 'absolute',
            right: 0,
            top: 0,
            content: '""',
            display: 'block',
            background: theme.palette.secondary.main,
            borderRadius: theme.shape.borderRadius,
          },
          '&:before': {
            display: 'none',
          },
          '& .iconContainer': {
            color: '#213a6d',
          },
        },
      },
    },
    appVer: {
      position: 'absolute',
      fontSize: '1rem',
      opacity: '0.7',
      top: '5px',
      right: '5px',
      width: '28px',
      textAlign: 'right',
    },
  };
};

interface NavigationMobileToolBarProps {
  children?: ReactNode;
  navigation: AppNS.NavigationElement[];
  mode: string;
}

export default function NavigationMobileToolBar(
  props: NavigationMobileToolBarProps
) {
  const { children, navigation, mode } = props;
  const theme = useTheme();
  const styles = getStyles(theme);

  const toolBar = navigation.filter((item: AppNS.NavigationElement) => {
    return item.show && item.main && !item.settings;
  });

  const nav = navigation.filter((item: AppNS.NavigationElement) => {
    return item.show && !item.settings && !item.main;
  });

  const settings = navigation.filter((item: AppNS.NavigationElement) => {
    return item.show && item.settings;
  });

  return (
    <Box sx={styles.root}>
      {mode === 'toolBar' && (
        <Box sx={styles.toolBar}>
          {toolBar.map((item: AppNS.NavigationElement) => (
            <NavigationElement element={item} key={item.id} />
          ))}
          {children}
        </Box>
      )}
      {mode === 'nav' && (
        <>
          <div className="mainPanel">
            {nav.map((item: AppNS.NavigationElement) => (
              <NavigationElement element={item} key={item.id} />
            ))}
          </div>
          <Box sx={styles.appVer}>v{APP_VER}</Box>

          <div className="settingsPanel">
            {settings?.map((item: AppNS.NavigationElement) => (
              <NavigationElement element={item} key={item.id} />
            ))}
            <LogoutButton />
          </div>
        </>
      )}
    </Box>
  );
}
